import {
  STATUS_MATCH_EVIDENCE_BASE,
  buildChallengeCardSubtitle,
  buildChallengeCardTitle,
  serializeChallengeCardBackground,
} from './challengeCard';
import { createI18nStringArrayFields, createI18nStringFields } from '../i18nFields';
import { resolveProgramDisplayName } from './programs';
import { parseNonEmptyStrings } from '../parseHelpers';
import { normalizeCampaignSlug } from './slug';
import type { StatusCampaignPayloadBuilderState, ProgramConfig } from './types';

export function buildPayload(state: StatusCampaignPayloadBuilderState): Record<string, unknown> {
  if (state.mode === 'statusClaim') {
    return buildStatusClaimPayload(state);
  }
  return buildStatusMatchPayload(state);
}

function buildStatusClaimPayload(state: StatusCampaignPayloadBuilderState): Record<string, unknown> {
  return {
    campaignId: state.campaignId.trim(),
    slug: normalizeCampaignSlug(state.slug),
    termsSlug: state.termsSlug.trim(),
    hero: {
      ...createI18nStringFields('title', state.heroTitle.trim()),
      ...createI18nStringFields('subtitle', state.heroSubtitle.trim()),
      imageId: state.heroImageId.trim(),
    },
    partner: {
      name: state.partnerName.trim(),
      logoImageId: state.partnerLogoImageId.trim(),
      ...createI18nStringFields('memberStatus', 'member'),
    },
    tiers: [...state.tiersGranted],
    secondarySection: {
      ...createI18nStringFields('title', state.secondaryTitle.trim()),
      ...createI18nStringFields('subtitle', state.secondarySubtitle.trim()),
    },
  };
}

function mapProgramTiers(program: ProgramConfig): Array<{ sourceTier: string; targetSocieteTier: string }> {
  return program.tierMappings
    .filter((mapping) => mapping.sourceTier.trim() && mapping.targetSocieteTier.trim())
    .map((mapping) => ({
      sourceTier: mapping.sourceTier.trim(),
      targetSocieteTier: mapping.targetSocieteTier.trim(),
    }));
}

function buildStatusMatchPayload(state: StatusCampaignPayloadBuilderState): Record<string, unknown> {
  const photoHints = parseNonEmptyStrings(state.evidencePhotoHints);
  const proofRequirements = parseNonEmptyStrings(state.evidenceProofRequirements);
  const cardTitle = buildChallengeCardTitle(state.challengeCardTitleSuffix);
  const cardSubtitle = buildChallengeCardSubtitle(state.challengeCardSubtitleBefore, state.challengeCardSubtitleAfter);
  const terms = parseNonEmptyStrings(state.terms);

  return {
    slug: normalizeCampaignSlug(state.slug),
    campaignId: state.campaignId.trim(),
    ...(state.allowedRegions !== undefined ? { allowedRegions: [...state.allowedRegions] } : {}),
    hero: {
      ...createI18nStringFields('title', state.heroTitle.trim()),
      ...createI18nStringFields('subtitle', state.heroSubtitle.trim()),
      ...createI18nStringFields('description', state.heroDescription.trim()),
      ...createI18nStringFields('ctaText', state.heroCtaText.trim()),
      imageId: state.heroImageId.trim(),
    },
    programs: state.programs
      .filter((program) => program.programName.trim())
      .map((program) => ({
        name: resolveProgramDisplayName(program.programName.trim()),
        logoImageId: program.logoImageId.trim(),
        tiers: mapProgramTiers(program),
      })),
    evidence: {
      ...STATUS_MATCH_EVIDENCE_BASE,
      minPhotos: state.evidenceMinPhotos,
      ...createI18nStringArrayFields('photoHints', photoHints),
      acceptedFormats: parseNonEmptyStrings(state.evidenceAcceptedFormats),
      ...createI18nStringArrayFields('proofRequirements', proofRequirements),
      verificationImageId: state.evidenceVerificationImageId.trim(),
    },
    conditions: state.conditions
      .filter((condition) => condition.title || condition.description)
      .map((condition) => ({
        icon: condition.icon,
        ...createI18nStringFields('title', condition.title),
        ...createI18nStringFields('description', condition.description),
      })),
    howItWorks: state.howItWorks
      .filter((step) => step.title || step.description)
      .map((step) => ({
        ...createI18nStringFields('title', step.title),
        ...createI18nStringFields('description', step.description),
      })),
    challengeCard: {
      background: serializeChallengeCardBackground(state.challengeCardBackground),
      ...createI18nStringFields('cardTitle', cardTitle),
      ...createI18nStringFields('cardSubtitle', cardSubtitle),
    },
    successSteps: state.successSteps.map((step) => ({
      key: step.key,
      ...createI18nStringFields('title', step.title),
      ...createI18nStringFields('description', step.description),
    })),
    faqs: state.faqs
      .filter((faq) => faq.question || faq.answer)
      .map((faq) => ({
        ...createI18nStringFields('question', faq.question),
        ...createI18nStringFields('answer', faq.answer),
      })),
    ...createI18nStringArrayFields('terms', terms),
    termsSlug: state.termsSlug.trim(),
  };
}

export function serializePayload(state: StatusCampaignPayloadBuilderState): string {
  return JSON.stringify(buildPayload(state), null, 2);
}

export function payloadDownloadFilename(state: StatusCampaignPayloadBuilderState): string {
  const slug = state.slug.trim();
  return slug ? `${slug}-payload.txt` : 'campaign-payload.txt';
}
